import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AiImageGeneration,
  AiImageReferenceInput,
  DamSearchAssetResult,
  ProductReferenceLookupResult,
} from '../types';
import { getAiImageAssetUrl } from '../utils';
import { ICategory } from '../../../types/damTypes';

type PickerTab = 'upload' | 'dam' | 'sku' | 'generated';

interface ReferencePickerProps {
  references: AiImageReferenceInput[];
  history: AiImageGeneration[];
  categories: ICategory[];
  maxReferences: number;
  onAddUploadedFiles: (files: FileList | File[]) => Promise<void>;
  onSearchDamAssets: (query: string, categoryId?: string) => Promise<DamSearchAssetResult[]>;
  onLookupSkuReference: (sku: string) => Promise<ProductReferenceLookupResult>;
  onAddDamAssetReference: (asset: DamSearchAssetResult) => void;
  onAddSkuProductReference: (asset: DamSearchAssetResult, sku: string) => void;
  onAddGeneratedReference: (generationId: string, outputId: string) => Promise<void>;
  onRemoveReference: (referenceId: string) => void;
  onReorderReferences?: (orderedIds: string[]) => void;
}

function getReferenceLabel(type: AiImageReferenceInput['type']): string {
  if (type === 'upload') return 'Uppladdad';
  if (type === 'dam_asset') return 'DAM';
  if (type === 'sku_product') return 'Produkt';
  return 'Genererad';
}

function getReferenceImageSrc(reference: {
  thumbnailUrl?: string;
  url?: string;
  dataUrl?: string;
}): string | undefined {
  if (reference.dataUrl) return reference.dataUrl;
  if (reference.thumbnailUrl) return getAiImageAssetUrl(reference.thumbnailUrl);
  if (reference.url) return getAiImageAssetUrl(reference.url);
  return undefined;
}

const SortableReferenceCard: React.FC<{
  reference: AiImageReferenceInput;
  onRemoveReference: (referenceId: string) => void;
  onPreview: (preview: { src: string; title: string }) => void;
}> = ({ reference, onRemoveReference, onPreview }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: reference.referenceId,
  });
  const imageSrc = getReferenceImageSrc(reference);
  const title = reference.title || reference.fileName || reference.sku || 'Referensbild';

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        p: 0.75,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.06)',
        minWidth: 0,
        opacity: isDragging ? 0.7 : 1,
        '&:focus-within': { borderColor: 'primary.main' },
      }}
    >
      <IconButton
        size="small"
        aria-label={`Flytta ${title}`}
        {...attributes}
        {...listeners}
        sx={{ flexShrink: 0, cursor: 'grab', width: 32, height: 32 }}
      >
        <DragIndicatorIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Tooltip title={imageSrc ? 'Visa större' : ''} disableHoverListener={!imageSrc}>
        <Avatar
          variant="rounded"
          src={imageSrc}
          alt={title}
          onClick={() => {
            if (!imageSrc) return;
            onPreview({ src: imageSrc, title });
          }}
          sx={{
            width: 40,
            height: 40,
            bgcolor: 'grey.200',
            flexShrink: 0,
            borderRadius: 1.5,
            cursor: imageSrc ? 'pointer' : 'default',
          }}
        >
          {reference.type === 'generated' ? <ReplayIcon /> : null}
        </Avatar>
      </Tooltip>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, lineHeight: 1.3 }} noWrap>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {getReferenceLabel(reference.type)}
        </Typography>
      </Box>
      <IconButton
        size="small"
        aria-label={`Ta bort ${title}`}
        onClick={() => onRemoveReference(reference.referenceId)}
        sx={{ flexShrink: 0, width: 32, height: 32 }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

const ReferencePicker: React.FC<ReferencePickerProps> = ({
  references,
  history,
  categories,
  maxReferences,
  onAddUploadedFiles,
  onSearchDamAssets,
  onLookupSkuReference,
  onAddDamAssetReference,
  onAddSkuProductReference,
  onAddGeneratedReference,
  onRemoveReference,
  onReorderReferences,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<PickerTab>('upload');
  const [damQuery, setDamQuery] = useState('');
  const [damResults, setDamResults] = useState<DamSearchAssetResult[]>([]);
  const [damLoading, setDamLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [skuQuery, setSkuQuery] = useState('');
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuResult, setSkuResult] = useState<ProductReferenceLookupResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const damDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skuDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [preview, setPreview] = useState<{ src: string; title: string } | null>(null);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const mainCategories = useMemo(
    () => categories.filter((c) => !c.parentCategory),
    [categories]
  );

  const generatedCandidates = useMemo(() => {
    return history.flatMap((generation) =>
      generation.outputs.map((output) => ({
        generationId: generation._id,
        outputId: output._id,
        thumbnailUrl: output.thumbnailUrl || output.mediumUrl || output.publicUrl,
        aspectRatio: generation.aspectRatio,
        width: output.width,
        height: output.height,
      }))
    );
  }, [history]);

  const searchDam = useCallback(async (query: string, categoryId?: string | null) => {
    if (query.trim().length < 2 && !categoryId) {
      setDamResults([]);
      return;
    }
    setDamLoading(true);
    try {
      const results = await onSearchDamAssets(query.trim(), categoryId || undefined);
      setDamResults(results);
    } finally {
      setDamLoading(false);
    }
  }, [onSearchDamAssets]);

  const lookupSku = useCallback(async (sku: string) => {
    if (!sku.trim()) {
      setSkuResult(null);
      return;
    }
    setSkuLoading(true);
    try {
      const result = await onLookupSkuReference(sku.trim());
      setSkuResult(result);
    } finally {
      setSkuLoading(false);
    }
  }, [onLookupSkuReference]);

  useEffect(() => {
    if (damDebounceRef.current) clearTimeout(damDebounceRef.current);
    if (damQuery.trim().length < 2 && !selectedCategoryId) {
      setDamResults([]);
      return;
    }
    damDebounceRef.current = setTimeout(() => { void searchDam(damQuery, selectedCategoryId); }, 400);
    return () => { if (damDebounceRef.current) clearTimeout(damDebounceRef.current); };
  }, [damQuery, selectedCategoryId, searchDam]);

  useEffect(() => {
    if (skuDebounceRef.current) clearTimeout(skuDebounceRef.current);
    if (!skuQuery.trim()) {
      setSkuResult(null);
      return;
    }
    skuDebounceRef.current = setTimeout(() => { void lookupSku(skuQuery); }, 500);
    return () => { if (skuDebounceRef.current) clearTimeout(skuDebounceRef.current); };
  }, [skuQuery, lookupSku]);

  const canAddMore = references.length < maxReferences;

  const handleReorder = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderReferences) return;
    const ids = references.map((reference) => reference.referenceId);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorderReferences(arrayMove(ids, oldIndex, newIndex));
  };

  const addUploaded = async (files: FileList | File[] | null) => {
    if (!files || (files instanceof FileList ? files.length === 0 : files.length === 0)) return;
    await onAddUploadedFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddPhotoAlternateIcon />}
            disabled={!canAddMore}
            onClick={() => setDialogOpen(true)}
            sx={{
              flexShrink: 0,
              borderRadius: 2,
              textTransform: 'none',
              borderColor: 'rgba(0,0,0,0.12)',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(25,118,210,0.04)' },
            }}
          >
            Referenser ({references.length}/{maxReferences})
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {references.length} valda. Dra för att ändra ordning. Ctrl+V klistrar in.
          </Typography>
        </Box>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder}>
          <SortableContext items={references.map((reference) => reference.referenceId)} strategy={rectSortingStrategy}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
              {references.map((reference) => (
                <SortableReferenceCard
                  key={reference.referenceId}
                  reference={reference}
                  onRemoveReference={onRemoveReference}
                  onPreview={setPreview}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            pt: 2.5,
            pb: 0,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Referensbilder
          </Typography>
          <IconButton aria-label="Stäng referensväljare" onClick={() => setDialogOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ px: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' },
            }}
          >
            <Tab value="upload" label="Ladda upp" />
            <Tab value="dam" label="Sök i Athena" />
            <Tab value="sku" label="Artikelnummer" />
            <Tab value="generated" label="Genererade" />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ px: 3, py: 2.5, minHeight: 300 }}>

          {/* ── Upload ── */}
          {activeTab === 'upload' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                py: 6,
                border: '2px dashed',
                borderColor: uploadDragOver ? 'primary.main' : 'rgba(0,0,0,0.1)',
                borderRadius: 3,
                cursor: 'pointer',
                bgcolor: uploadDragOver ? 'rgba(25,118,210,0.06)' : 'transparent',
                transition: 'border-color 0.2s, background 0.2s',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(25,118,210,0.03)' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
              }}
              role="button"
              tabIndex={0}
              aria-label="Ladda upp referensbilder"
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setUploadDragOver(true);
              }}
              onDragLeave={() => setUploadDragOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setUploadDragOver(false);
                void addUploaded(event.dataTransfer.files);
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Klicka eller släpp bilder från datorn
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                JPG, PNG, WebP. Dialogen stannar öppen så du kan lägga till fler.
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(event) => {
                  void addUploaded(event.target.files);
                }}
              />
            </Box>
          )}

          {/* ── Athena search ── */}
          {activeTab === 'dam' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Börja skriva för att söka i Athena..."
                value={damQuery}
                onChange={(event) => setDamQuery(event.target.value)}
                autoFocus
                slotProps={{
                  input: {
                    endAdornment: damLoading ? <CircularProgress size={18} /> : null,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2.5 },
                }}
              />

              {mainCategories.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {mainCategories.map((cat) => (
                    <Chip
                      key={cat._id}
                      label={cat.name}
                      size="small"
                      variant={selectedCategoryId === cat._id ? 'filled' : 'outlined'}
                      color={selectedCategoryId === cat._id ? 'primary' : 'default'}
                      onClick={() =>
                        setSelectedCategoryId((prev) => (prev === cat._id ? null : cat._id))
                      }
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </Box>
              )}

              {damResults.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 1,
                    maxHeight: 340,
                    overflowY: 'auto',
                  }}
                >
                  {damResults.map((asset) => (
                    <Box
                      key={asset._id}
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        '&:hover .dam-overlay': { opacity: 1 },
                      }}
                      onClick={() => { if (canAddMore) onAddDamAssetReference(asset); }}
                    >
                      <Avatar
                        variant="rounded"
                        src={getAiImageAssetUrl(asset.thumbnailUrl)}
                        sx={{ width: '100%', height: 100, bgcolor: 'grey.100', borderRadius: 2 }}
                      />
                      <Box
                        className="dam-overlay"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.45)',
                          opacity: 0,
                          transition: 'opacity 0.15s',
                          borderRadius: 2,
                        }}
                      >
                        <AddCircleOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 500, fontSize: '0.65rem' }} noWrap>
                        {asset.title || asset.fileName}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {!damLoading && (damQuery.trim().length >= 2 || selectedCategoryId) && damResults.length === 0 && (
                <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                  Inga resultat hittades.
                </Typography>
              )}

              {damQuery.trim().length < 2 && !selectedCategoryId && (
                <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                  Skriv minst 2 tecken eller välj en kategori för att söka.
                </Typography>
              )}
            </Box>
          )}

          {/* ── SKU lookup ── */}
          {activeTab === 'sku' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ange artikelnummer..."
                value={skuQuery}
                onChange={(event) => setSkuQuery(event.target.value)}
                autoFocus
                slotProps={{
                  input: {
                    endAdornment: skuLoading ? <CircularProgress size={18} /> : null,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2.5 },
                }}
              />

              {skuResult?.found && skuResult.asset && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                  onClick={() => {
                    if (!canAddMore) return;
                    onAddSkuProductReference(skuResult.asset!, skuQuery.trim());
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={getAiImageAssetUrl(skuResult.asset.thumbnailUrl)}
                    sx={{ width: 72, height: 72, borderRadius: 2 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {skuResult.asset.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {[skuResult.asset.productName, skuResult.asset.articleNumber].filter(Boolean).join(' | ')}
                    </Typography>
                  </Box>
                  <AddCircleOutlineIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                </Box>
              )}

              {skuResult && !skuResult.found && (
                <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                  Ingen produktbild hittades.
                </Typography>
              )}

              {!skuResult && !skuLoading && !skuQuery.trim() && (
                <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                  Ange ett artikelnummer för att söka.
                </Typography>
              )}
            </Box>
          )}

          {/* ── Generated images ── */}
          {activeTab === 'generated' && (
            <Box>
              {generatedCandidates.length > 0 ? (
                <Box
                  sx={{
                    columnCount: { xs: 2, sm: 3, md: 4 },
                    columnGap: '8px',
                    maxHeight: 420,
                    overflowY: 'auto',
                  }}
                >
                  {generatedCandidates.map((candidate) => (
                    <Box
                      key={`${candidate.generationId}-${candidate.outputId}`}
                      sx={{
                        breakInside: 'avoid',
                        mb: '8px',
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        '&:hover .gen-overlay': { opacity: 1 },
                      }}
                      onClick={async () => {
                        if (!canAddMore) return;
                        await onAddGeneratedReference(candidate.generationId, candidate.outputId);
                      }}
                    >
                      <Box
                        component="img"
                        src={getAiImageAssetUrl(candidate.thumbnailUrl)}
                        alt=""
                        sx={{
                          width: '100%',
                          display: 'block',
                          aspectRatio: candidate.width && candidate.height
                            ? `${candidate.width} / ${candidate.height}`
                            : '4 / 5',
                          objectFit: 'cover',
                          bgcolor: 'grey.100',
                        }}
                      />
                      <Box
                        className="gen-overlay"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.4)',
                          opacity: 0,
                          transition: 'opacity 0.15s',
                        }}
                      >
                        <AddCircleOutlineIcon sx={{ color: '#fff', fontSize: 36 }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 4 }}>
                  Inga genererade bilder finns ännu.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {references.length} av {maxReferences} valda{!canAddMore ? ' · max antal nått' : ''}
          </Typography>
          <Button variant="contained" onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Klar
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
            width: 380,
            maxWidth: 'calc(100vw - 32px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>
            {preview?.title || 'Referensbild'}
          </Typography>
          <IconButton aria-label="Stäng förhandsvisning" size="small" onClick={() => setPreview(null)} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {preview && (
          <Box
            component="img"
            src={preview.src}
            alt={preview.title}
            sx={{
              display: 'block',
              width: '100%',
              maxHeight: 360,
              objectFit: 'contain',
              bgcolor: 'grey.100',
            }}
          />
        )}
      </Dialog>
    </>
  );
};

export default ReferencePicker;
